/**
 * The book's ending, typographically — shared by the report and the practice
 * result.
 *
 * Both screens are the *last pages of the book*: they are printed on the book's
 * paper, in the book's own type, and their words follow the language the reader is
 * reading the application in. Two screens that must look like one page cannot each
 * decide what a caption is, so the decisions live here once.
 */
import {useMemo} from 'react'
import {useLingui} from '@lingui/react/macro'

import {directionStyle, directionTextStyle} from '#/lib/flashbookDirection'
import {copyDirection, type FlashBookReportModel} from '#/lib/flashbookReport'
import {usePlayTextStyle} from '#/components/flashbooks/FlashBookPlayTheme'

/**
 * A duration as a reader says it: `42s`, `4m 12s`, `1h 05m`.
 *
 * Deliberately not assembled from translated unit words: the digits and their
 * order are the same in every language the app ships, and a translated
 * abbreviation of "minute" beside a count would be longer than the number it
 * labels. The unit letters follow the locale's own convention by staying as the
 * reader expects to see them on a stopwatch.
 *
 * Here rather than in the report because the ending is not one screen any more: a
 * shared report card prints the same clock, and two formatters would be two
 * answers to "how long did that take".
 */
export function formatDuration(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000))
  if (total < 60) return `${total}s`
  const minutes = Math.floor(total / 60)
  if (minutes < 60) return `${minutes}m ${String(total % 60).padStart(2, '0')}s`
  return `${Math.floor(minutes / 60)}h ${String(minutes % 60).padStart(2, '0')}m`
}

/**
 * How the read went, in words — the one verdict of a book's ending.
 *
 * The report and the shared card are the same verdict on two surfaces, and a
 * learner who reads "Strong read" on their report must not see "Good job" on the
 * picture they post: the words live here so that cannot happen, and each band keeps
 * the *piece of advice* it was written with (three shades of "quite good" would be
 * three ways of saying nothing).
 */
export function useReportVerdict(model: FlashBookReportModel): {
  title: string
  subtitle: string
} {
  const {t: l} = useLingui()
  const {counts, score} = model
  if (counts.exercises === 0) {
    return {
      title: l`The end`,
      subtitle:
        counts.readings > 0
          ? l`Read cover to cover — no exercises in this book.`
          : l`You finished the book. Great focus!`,
    }
  }
  switch (model.band) {
    case 'perfect':
      return {
        title: l`Perfect read`,
        subtitle: l`Every one of the ${counts.exercises} exercises, right.`,
      }
    case 'strong':
      return {
        title: l`Strong read`,
        subtitle: l`${counts.correct} of ${counts.exercises} correct, ${score.pct}% of the points.`,
      }
    case 'steady':
      return {
        title: l`Getting there`,
        subtitle: l`${counts.correct} of ${counts.exercises} correct — the rest is close.`,
      }
    case 'shaky':
      return {
        title: l`Worth another read`,
        subtitle: l`${counts.wrong} questions to go over. Page by page, below.`,
      }
    default:
      return {
        title: l`Finished`,
        subtitle: l`You turned every page without answering — ${counts.skipped} questions are waiting.`,
      }
  }
}

/**
 * A reading *of the app*, not of the book.
 *
 * Deliberately the interface language rather than the book's own direction: a
 * number, a label and a heading are the application talking *about* the read, and a
 * French reader looking at an Arabic book still reads the report in French — laid
 * out the way French is written.
 */
export function useCopyStyle() {
  const {i18n} = useLingui()
  const dir = copyDirection(i18n.locale)
  return useMemo(
    () => ({
      dir,
      text: directionTextStyle(dir),
      row: dir === 'rtl' ? directionStyle('rtl') : null,
    }),
    [dir],
  )
}

/** The book's own type roles: display, title, question, body, label, caption. */
export function useReportType() {
  return {
    display: usePlayTextStyle('display'),
    title: usePlayTextStyle('title'),
    question: usePlayTextStyle('question'),
    body: usePlayTextStyle('body'),
    label: usePlayTextStyle('label'),
    caption: usePlayTextStyle('caption'),
  }
}
